import { JSX } from "react";
import { motion } from "framer-motion";

interface Testimonial {
  text: string;
  name: string;
  role: string;
  imageUrl: string;
  clinic?: string;
}

const styles = {
  section: "bg-gray-50 py-16 overflow-hidden",
  container: "px-6 py-8 mx-auto",
  title: "text-3xl font-bold text-center text-gray-900 mb-12",
  highlight: "text-green-600",
  marqueeWrapper: "relative overflow-hidden",
  marqueeTrack: "flex gap-8 py-4",
  trackInner: "flex gap-8",
  card: "flex-shrink-0 w-[400px] p-8 bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300",
  cardInner: "flex flex-col h-full justify-between",
  text: "text-gray-600 leading-relaxed mb-6 text-lg italic",
  userRow: "flex items-center mt-auto",
  avatar: "object-cover rounded-full w-14 h-14",
  userInfo: "ml-4 flex flex-col",
  userName: "font-bold text-gray-900",
  userRole: "text-sm text-gray-500",
  clinicTag: "text-xs text-green-600 font-medium",
  gradientLeft:
    "absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none",
  gradientRight:
    "absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none",
};

const testimonials: Testimonial[] = [
  {
    text: "Gracias a AppCotas, hemos logrado organizar nuestra información de forma mucho más eficiente. La plataforma es fácil de usar, y el equipo de soporte siempre está ahí cuando lo necesitamos.",
    name: "Roberto García",
    role: "Director Clínico",
    clinic: "Mundo Animal",
    imageUrl:
      "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=200&q=80",
  },
  {
    text: "AppCotas ha cambiado por completo la forma en que organizamos nuestra información. Es tan fácil de usar que todo el equipo se adaptó rápidamente, y el soporte siempre está ahí ayudarnos.",
    name: "Mía Pérez",
    role: "Fundadora",
    clinic: "PetCare Plus",
    imageUrl:
      "https://images.unsplash.com/photo-1499470932971-a90681ce8530?w=200&q=80",
  },
  {
    text: "La mejor decisión que tomamos fue implementar AppCotas. Nuestros tiempos de atención se redujeron un 40% y la satisfacción del cliente subió exponencialmente.",
    name: "Carlos López",
    role: "Veterinario Senior",
    clinic: "Clínica Central",
    imageUrl:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80",
  },
  {
    text: "Con el historial médico digital, puedo acceder a la información de cualquier paciente en segundos. Esto ha mejorado enormemente la calidad de atención que brindamos.",
    name: "Ana Martínez",
    role: "Especialista Felina",
    clinic: "Centro Felino",
    imageUrl:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80",
  },
  {
    text: "Los recordatorios automáticos han reducido nuestras inasistencias en un 35%. Es increíble cómo algo tan simple puede tener un impacto tan grande en nuestra facturación.",
    name: "Pedro Sánchez",
    role: "Administrador",
    clinic: "Veterinaria San José",
    imageUrl:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80",
  },
  {
    text: "La plataforma es intuitiva y el soporte es excepcional. Cualquier duda que tengo, me la resuelven casi al instante. Totalmente recomendado.",
    name: "Laura Gómez",
    role: "Coordinadora",
    clinic: "Red de Clínicas Vet",
    imageUrl:
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80",
  },
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({
  testimonial,
}) => (
  <div className={styles.card}>
    <div className={styles.cardInner}>
      <p className={styles.text}>"{testimonial.text}"</p>
      <div className={styles.userRow}>
        <img
          className={styles.avatar}
          src={testimonial.imageUrl}
          alt={`Foto de ${testimonial.name}`}
          width={56}
          height={56}
        />
        <div className={styles.userInfo}>
          <span className={styles.userName}>{testimonial.name}</span>
          <span className={styles.userRole}>{testimonial.role}</span>
          {testimonial.clinic && (
            <span className={styles.clinicTag}>{testimonial.clinic}</span>
          )}
        </div>
      </div>
    </div>
  </div>
);

const TestimonialsSection = (): JSX.Element => {
  const duplicatedTestimonials = [
    ...testimonials,
    ...testimonials,
    ...testimonials,
  ];

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <motion.h2
          className={styles.title}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Lo que dicen nuestros{" "}
          <span className={styles.highlight}>clientes</span>
        </motion.h2>

        <div className={styles.marqueeWrapper}>
          <div className={styles.gradientLeft} />
          <div className={styles.gradientRight} />

          <motion.div
            className={styles.marqueeTrack}
            animate={{ x: ["0%", "-33.333%"] }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 30,
                ease: "linear",
              },
            }}
          >
            <div className={styles.trackInner}>
              {duplicatedTestimonials.map((testimonial, index) => (
                <TestimonialCard
                  key={`${testimonial.name}-${index}`}
                  testimonial={testimonial}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
